/**
 * Envelope are currently ONLY available when ordering another card type.
 */
const ENVELOPE_CARD_TYPES = ['ENVELOPE', 'INVITATION_ENVELOPE', 'RSVP_ENVELOPE'] as const;
export type EnvelopeCardType = (typeof ENVELOPE_CARD_TYPES)[number];
export const isEnvelopeCardType = (type?: string): type is EnvelopeCardType => {
  if (!type) return false;
  return ENVELOPE_CARD_TYPES.includes(type as EnvelopeCardType);
};
export const isEnvelope = (type?: string): type is 'ENVELOPE' => type === 'ENVELOPE';

/**
 * The "real" individual paper add-on card types (sent to & from the API)
 */
export const PAPER_ADD_ONS_LEAD_CARD_TYPES = ['BELLY_BAND', 'VELLUM_JACKET', 'WAX_SEAL'] as const;
export type PaperAddOnLeadCardType = (typeof PAPER_ADD_ONS_LEAD_CARD_TYPES)[number];
export const isPaperAddOnLeadCardType = (type?: string): type is PaperAddOnLeadCardType => {
  if (!type) return false;
  return PAPER_ADD_ONS_LEAD_CARD_TYPES.includes(type as PaperAddOnLeadCardType);
};

/**
 * Paper add ons lead card types plus "PAPER_ADD_ONS" (a FE-specific
 * value used to group the other add-on types together)
 */
const PAPER_ADD_ONS_CARD_TYPES = [...PAPER_ADD_ONS_LEAD_CARD_TYPES, 'PAPER_ADD_ONS'] as const;
export type PaperAddOnCardType = (typeof PAPER_ADD_ONS_CARD_TYPES)[number];
export const isPaperAddOnCardType = (type?: string): type is PaperAddOnCardType => {
  if (!type) return false;
  return PAPER_ADD_ONS_CARD_TYPES.includes(type as PaperAddOnCardType);
};

/**
 * Lead card types have their own PLP / PDP
 */
export const LEAD_CARD_TYPES = [
  ...PAPER_ADD_ONS_CARD_TYPES,
  'CHANGE_THE_DATE',
  'DETAILS',
  'DIGITAL_SAVE_THE_DATE',
  'ENCLOSURE',
  'HOLIDAY',
  'INVITATION',
  'MATCHING_SUITES',
  'MENU',
  'NAPKIN',
  'PLACE',
  'PROGRAM',
  'SAVE_THE_DATE',
  'SIGNAGE',
  'STICKER',
  'TABLE_NUMBER',
  'THANK_YOU',
  'WEDDING_SHOWER_INVITATION',
] as const;
export type LeadCardType = (typeof LEAD_CARD_TYPES)[number];
export const isLeadCardType = (type?: string): type is LeadCardType => {
  if (!type) return false;
  return (LEAD_CARD_TYPES as Readonly<string[]>).includes(type);
};

/**
 * Secondary card types are ONLY available through a lead card type's customization flow
 */
export const SECONDARY_CARD_TYPES = ['RSVP'] as const;
export type SecondaryCardType = (typeof SECONDARY_CARD_TYPES)[number];
export const isSecondaryCardType = (type?: string): type is SecondaryCardType => {
  if (!type) return false;
  return (SECONDARY_CARD_TYPES as Readonly<string[]>).includes(type);
};

/**
 * All card types combined
 */
export const CARD_TYPES = [
  ...LEAD_CARD_TYPES,
  ...SECONDARY_CARD_TYPES,
  ...ENVELOPE_CARD_TYPES,
] as const;
export type CardType = (typeof CARD_TYPES)[number];
export const isCardType = (type?: string): type is CardType => {
  if (!type) return false;
  return (CARD_TYPES as Readonly<string[]>).includes(type);
};

/**
 * A map including every card type
 */
export const CARD_TYPE_MAP = {
  invitation: 'INVITATION',
  invitationEnvelope: 'INVITATION_ENVELOPE',
  std: 'SAVE_THE_DATE',
  dstd: 'DIGITAL_SAVE_THE_DATE',
  ctd: 'CHANGE_THE_DATE',
  thankyou: 'THANK_YOU',
  menu: 'MENU',
  program: 'PROGRAM',
  place: 'PLACE',
  holiday: 'HOLIDAY',
  details: 'DETAILS',
  enclosure: 'ENCLOSURE',
  rsvp: 'RSVP',
  rsvpEnvelope: 'RSVP_ENVELOPE',
  envelope: 'ENVELOPE',
  matchingSuites: 'MATCHING_SUITES',
  weddingShower: 'WEDDING_SHOWER_INVITATION',
  napkin: 'NAPKIN',
  signage: 'SIGNAGE',
  sticker: 'STICKER',
  tableNumber: 'TABLE_NUMBER',
  paperAddOns: 'PAPER_ADD_ONS',
  bellyBand: 'BELLY_BAND',
  vellumJacket: 'VELLUM_JACKET',
  waxSeal: 'WAX_SEAL',
} as const;
